/**
 * チャンクメッセージ処理用カスタムフック
 */

import { useCallback, useRef } from 'react';

import { type EWBCIResponse } from '@/feature/api-client';
import Log from '@/Logger';

import { type EWBCIChunkedMessage } from '../core/types';
import { EWBCIError, EWBCIErrorType } from '../utils/errors';
import { restoreChunkedData } from '../utils/chunk';

/**
 * チャンクデータの管理用型
 */
interface ChunkData {
    /** 受信したチャンク番号のセット */
    receivedChunks: Set<number>;
    /** 各チャンクのデータ */
    chunks: Map<number, string>;
    /** 総チャンク数 */
    totalChunks: number;
    /** リクエストID */
    requestId: string;
    /** 受信時タイムスタンプ */
    timestamp: number;
}

/**
 * チャンクメッセージ処理の設定オプション
 */
export interface ChunkedMessageOptions {
    /**
     * 完成しないチャンクを破棄するまでの時間[ms]
     * 実際のチェックは3分おきに実施する
    */
    chunkTimeout?: number;
    /** チャンク完成時のコールバック */
    onComplete?: (completeMessage: EWBCIResponse) => void;
}

/**
 * チャンクメッセージ処理フックの戻り値
 */
export interface ChunkedMessageHookResult {
    /** チャンクを処理する関数（外部API） */
    processChunk: (chunk: EWBCIChunkedMessage) => Promise<void>;
    /** 全チャンクデータをクリアする関数（クリーンアップ用） */
    clearAllChunks: () => void;
}

/**
 * チャンクメッセージの分割・結合処理を行うカスタムフック
 * 
 * 大容量データの転送時に使用されるチャンクメッセージを管理し、
 * 分割されたデータを適切に結合する機能を提供する。
 * 
 * @param options 設定オプション
 * @returns チャンクメッセージ処理用の関数群
 */
export function useChunkedMessage(options: ChunkedMessageOptions = {}): ChunkedMessageHookResult {
    const { chunkTimeout, onComplete } = options;

    // チャンクデータを管理するMap（requestId -> ChunkData）
    const chunksMapRef = useRef<Map<string, ChunkData>>(new Map());
    const cleanupTimer = useRef<NodeJS.Timeout | null>(null);

    /**
     * チャンクメッセージを処理し、内部ストレージに保存
     */
    const processChunk = useCallback(async (chunk: EWBCIChunkedMessage): Promise<void> => {
        const logger = Log.getLogger();
        const { requestId, body } = chunk;
        const { n: totalChunks, i: chunkIndex, d: data } = body;
        const chunkNo = chunkIndex + 1; // 0-based から 1-based に変換
        try {
            logger.api.debug("useChunkedMessage", `Processing chunk ${chunkNo}/${totalChunks} for request ${requestId}`);

            // チャンクデータを取得または初期化
            let chunkData = chunksMapRef.current.get(requestId);
            if (!chunkData) {
                chunkData = {
                    requestId,
                    receivedChunks: new Set(),
                    chunks: new Map(),
                    totalChunks,
                    timestamp: Date.now(),
                };
                chunksMapRef.current.set(requestId, chunkData);
            }

            // チャンク番号の妥当性チェック
            if (chunkNo < 1 || chunkNo > totalChunks) {
                throw new Error(`Invalid chunk number: ${chunkNo} (total: ${totalChunks})`);
            }

            // 総チャンク数の整合性チェック
            if (chunkData.totalChunks !== totalChunks) {
                throw new Error(`Total chunks mismatch: expected ${chunkData.totalChunks}, got ${totalChunks}`);
            }

            // 重複チェック
            if (chunkData.receivedChunks.has(chunkNo)) {
                logger.api.warn("useChunkedMessage", `Duplicate chunk received: ${chunkNo} for request ${requestId}`);
                return;
            }

            // チャンクデータを保存
            chunkData.receivedChunks.add(chunkNo);
            chunkData.chunks.set(chunkNo, data);

            logger.api.debug("useChunkedMessage", `Chunk ${chunkNo} processed. Progress: ${chunkData.receivedChunks.size}/${totalChunks}`);

            // チャンクが完成したかチェック
            if (chunkData.receivedChunks.size === totalChunks) {
                logger.api.info("useChunkedMessage", `All chunks received for request ${requestId}. Auto-combining...`);

                // データを取り出して
                const data = chunksMapRef.current.get(requestId);
                // 先にクリーンアップ
                chunksMapRef.current.delete(requestId);

                // 非同期処理は別途実行
                if (!data) throw new Error("chunkData missing after all chunks received.");
                if (!onComplete) {
                    // コールバックが設定されていない場合はログを出して終了
                    logger.api.debug("useChunkedMessage", `chnkMng has no callback, so message: ${requestId} ignored.`);
                    return;
                }
                // 自動的にチャンクを結合
                const utf8str = await combineChunks(requestId, data);

                if (!utf8str)
                    throw new Error(`Error auto-combining chunks for ${requestId}: combined message is null.`);
                let message: any = JSON.parse(utf8str);
                // 【互換処理】本来ココではmessage.bodyをrespとして取れるのが正しい。agentの不具合だがいったんこのまま。
                if (message.__type === "response" && message.app === "ewbci" && message.title === "response") {
                    message = message.body; // agentの不具合対応
                    message.__typename = "EWBCIResponse";
                }
                // 完成したらコールバック
                onComplete(message as EWBCIResponse);
            }
            // [Optional] タイムアウト処理
            if (chunkTimeout) {
                if (chunksMapRef.current.size > 0 && !cleanupTimer.current) {
                    // チャンクデータがあればクリーンアップタイマーを起動(3分)
                    cleanupTimer.current = setInterval(() => {
                        if (!chunkTimeout) return;
                        const now = Date.now();
                        Array.from(chunksMapRef.current.keys()).forEach((requestId: string) => {
                            const d = chunksMapRef.current.get(requestId);
                            if (d === undefined) return;
                            if (d.timestamp + chunkTimeout > now) return;
                            // チェック時点で最初のチャンク受信時刻からchunkTimeout[ms]過ぎていたら捨てる
                            logger.api.error("useChunkedMessage", `timeout waiting chunkData: [${requestId}] ${d.receivedChunks.size}/${d.totalChunks}`);
                            chunksMapRef.current.delete(requestId);
                        });
                    }, 3 * 60 * 1000);
                }

                if (chunksMapRef.current.size === 0 && cleanupTimer.current) {
                    // チャンクデータがなくなればクリーンアップタイマーを停止
                    clearInterval(cleanupTimer.current);
                    cleanupTimer.current = null;
                }
            }
        } catch (error) {
            // エラーはここで集約してEWBCIError(ChunkError)に変換して投げる
            throw new EWBCIError(`Error processing chunk ${chunkIndex} for request ${requestId}`, EWBCIErrorType.ChunkError, error);
        }
    }, [chunkTimeout, onComplete]);

    /**
     * 全チャンクデータをクリアする（クリーンアップ用）
     */
    const clearAllChunks = useCallback((): void => {
        const logger = Log.getLogger();
        const size = chunksMapRef.current.size;
        chunksMapRef.current.clear();
        if (size > 0) {
            logger.api.info("useChunkedMessage", `Cleared all ${size} chunk data sets`);
        }
    }, []);

    return {
        processChunk,
        clearAllChunks,
    };
}

/**
 * 完了したチャンクデータを結合して返す
 */
async function combineChunks(requestId: string, chunkData: ChunkData): Promise<string | null> {
    const logger = Log.getLogger();
    // チャンク番号順にデータを収集
    const orderedChunks: string[] = [];
    for (let i = 1; i <= chunkData.totalChunks; i++) {
        const chunk = chunkData.chunks.get(i);
        if (!chunk) {
            throw new Error(`Missing chunk ${i} for request ${requestId}`);
        }
        orderedChunks.push(chunk);
    }

    // base64デコード→Uint8Array結合→gzip解凍→JSON文字列
    const result = await restoreChunkedData(orderedChunks);

    logger.api.info("useChunkedMessage", `Successfully combined and restored ${chunkData.totalChunks} chunks for request ${requestId}. Restored size: ${result.length}`);

    return result;
}
